import { useEffect, useState } from 'react'
import { client, type AppNotification } from '../lib/api'

function relativeTime(iso: string): string {
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) return ''
  const mins = Math.round((Date.now() - t) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  return `${days}d ago`
}

function typeIcon(type: string): string {
  if (type === 'match') return '✦'
  if (type === 'like') return '♡'
  if (type === 'view') return '◎'
  if (type === 'message') return '✉'
  return '•'
}

export function NotificationsModal({
  open,
  onClose,
  onNavigate,
  notify,
  onUnreadChange,
}: {
  open: boolean
  onClose: () => void
  onNavigate: (path: string) => void
  notify: (msg: string) => void
  onUnreadChange: (n: number) => void
}) {
  const [items, setItems] = useState<AppNotification[] | null>(null)
  const [loading, setLoading] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try {
      const res = await client.notifications()
      setItems(res.notifications)
      onUnreadChange(res.unread)
    } catch (e) {
      notify((e as Error).message)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!open) return
    void refresh()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  const openItem = async (n: AppNotification) => {
    if (!n.readAt) {
      try {
        await client.readNotifications([n.id])
        setItems((list) =>
          list
            ? list.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x))
            : list,
        )
        onUnreadChange(Math.max(0, (items?.filter((x) => !x.readAt && x.id !== n.id).length ?? 0)))
      } catch {
        /* ignore */
      }
    }
    const peerId = typeof n.data?.peerId === 'string' ? n.data.peerId : null
    onClose()
    if (n.type === 'message') onNavigate('/messages')
    else if (n.type === 'match') onNavigate('/messages')
    else if (n.type === 'like' || n.type === 'view') onNavigate('/likes')
    else if (peerId) onNavigate('/likes')
  }

  return (
    <div className="modal-backdrop notifications-backdrop" onClick={onClose}>
      <div
        className="modal notifications-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Notifications"
      >
        <div className="notifications-modal-head">
          <div>
            <span className="eyebrow">Activity</span>
            <h2>Notifications</h2>
          </div>
          <div className="notifications-modal-actions">
            <button
              type="button"
              className="text-button"
              onClick={() => {
                void client
                  .readNotifications(undefined, true)
                  .then(() => {
                    setItems((list) =>
                      list ? list.map((x) => ({ ...x, readAt: x.readAt ?? new Date().toISOString() })) : list,
                    )
                    onUnreadChange(0)
                  })
                  .catch((e) => notify((e as Error).message))
              }}
            >
              Mark all read
            </button>
            <button type="button" className="icon-button" onClick={onClose} aria-label="Close notifications">
              ×
            </button>
          </div>
        </div>

        {loading && !items ? (
          <div className="loading-center">
            <div className="spinner" />
          </div>
        ) : !items?.length ? (
          <div className="empty" style={{ padding: '28px 12px' }}>
            <div>◎</div>
            <h2>You're all caught up</h2>
            <p>Favorites, matches, views, and messages show up here in real time.</p>
          </div>
        ) : (
          <ul className="notifications-list">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className={`notifications-item${!n.readAt ? ' is-unread' : ''}`}
                  onClick={() => void openItem(n)}
                >
                  <span className="notifications-item-icon" aria-hidden>
                    {typeIcon(n.type)}
                  </span>
                  <span className="notifications-item-copy">
                    <strong>{n.title}</strong>
                    {n.body ? <small>{n.body}</small> : null}
                    <em>{relativeTime(n.createdAt)}</em>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
